import React from "react";
import {
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
} from "react-icons/fa";

interface MessageAttachmentCardProps {
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadID?: number;
}

/**
 * Utility function to format file size in human-readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

/**
 * Utility function to get appropriate icon based on file type
 */
const getFileIcon = (fileType: string) => {
  const type = fileType.toLowerCase();
  
  if (type.includes("pdf")) {
    return <FaFilePdf className="message-attachment-icon" style={{ color: "#dc2626" }} />;
  }
  if (type.includes("doc")) {
    return <FaFileWord className="message-attachment-icon" style={{ color: "#2563eb" }} />;
  }
  if (type.includes("xls") || type.includes("csv")) {
    return <FaFileExcel className="message-attachment-icon" style={{ color: "#16a34a" }} />;
  }
  if (type.includes("txt")) {
    return <FaFileAlt className="message-attachment-icon" style={{ color: "#6b7280" }} />;
  }
  
  return <FaFile className="message-attachment-icon" style={{ color: "#4a9d6f" }} />;
};

/**
 * Utility function to get file extension from filename
 */
const getFileExtension = (fileName: string): string => {
  const extension = fileName.split(".").pop()?.toUpperCase();
  return extension || "FILE";
};

/**
 * MessageAttachmentCard Component
 * 
 * Displays an attachment card within a chat message bubble.
 * Shows file icon, name, size, and type information.
 * 
 * @param fileName - The name of the attached file
 * @param fileSize - The size of the file in bytes
 * @param fileType - The MIME type or extension of the file
 * @param uploadID - Optional upload ID for future download functionality
 */
export const MessageAttachmentCard: React.FC<MessageAttachmentCardProps> = ({
  fileName,
  fileSize,
  fileType,
  uploadID,
}) => {
  const fileExtension = getFileExtension(fileName);
  
  return (
    <div className="message-attachment-card">
      {/* File type icon */}
      {getFileIcon(fileType)}
      
      {/* File information */}
      <div className="message-attachment-info">
        <div className="message-attachment-name" title={fileName}>
          {fileName}
        </div>
        <div className="message-attachment-meta">
          {fileExtension} • {formatFileSize(fileSize)}
        </div>
      </div>
      
      {/* Future enhancement: Add download button here */}
      {/* {uploadID && (
        <button
          className="message-attachment-download"
          onClick={() => handleDownload(uploadID)}
          aria-label="Download file"
          title="Download file"
        >
          <FaDownload />
        </button>
      )} */}
    </div>
  );
};

export default MessageAttachmentCard;